import { makeStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../theme/customColors";
import { HAKLAI_MONEY_INPUT_WIDTH } from "../shared/haklaiConstants";
import { HAKLAI_SECTION_LABEL_COLUMN_WIDTH } from "../shared/ui/HaklaiSectionLabelCell";
import {
  buildHaklaiFieldShellStyles,
  buildHaklaiRowFlashStyles,
  buildHaklaiSectionChromeStyles,
  HAKLAI_FIELD_ERROR_SLOT_WIDTH,
  haklaiAddPillStyles,
  haklaiLockableSectionStyles,
  haklaiStickyHeaderStyles,
  haklaiTableWrapperBaseStyles
} from "../shared/table/haklaiTableShared";

// Column widths are sized to host the centered pill inputs. Error "!" indicators
// are absolutely positioned beside the input so they do not shift the value off
// the column midpoint under each header.
export const EXPENSE_TYPE_COLUMN_WIDTH = 152;
export const AMOUNT_COLUMN_WIDTH = 124;
export const CURRENCY_COLUMN_WIDTH = 56;
// Word labels need a wider cell than the ₪ and $ signs. The column stays at
// the sign width unless one of those words is actually on screen.
const CURRENCY_COLUMN_WIDTH_BY_VALUE = {
  newShekel: CURRENCY_COLUMN_WIDTH,
  usd: CURRENCY_COLUMN_WIDTH,
  oldShekel: 72,
  lira: 72
};
export const getHaklaiCurrencyColumnWidth = (currencies = []) => (
  (currencies.length ? currencies : ["newShekel"]).reduce((widest, currency) => (
    Math.max(widest, CURRENCY_COLUMN_WIDTH_BY_VALUE[currency] || CURRENCY_COLUMN_WIDTH)
  ), CURRENCY_COLUMN_WIDTH)
);
export const DATE_COLUMN_WIDTH = 140;
export const DEPRECIATION_COLUMN_WIDTH = 56;
// Measured widest button: "לירה" + 9,999,999,999 + calculator icon is 152px.
// The column content box keeps 4px of slack so that value is not clipped, and
// this column does not take leftover table width.
export const DEPRECIATION_RESULT_CONTROL_WIDTH = 156;
export const DEPRECIATION_RESULT_COLUMN_WIDTH = DEPRECIATION_RESULT_CONTROL_WIDTH;
export const ZABAN_COLUMN_WIDTH = 110;
export const ADD_ROW_COLUMN_WIDTH = 32;
export const ACTION_COLUMN_WIDTH = 32;

export const AMOUNT_INPUT_WIDTH = HAKLAI_MONEY_INPUT_WIDTH;
export const DATE_INPUT_WIDTH = 128;
const DEPRECIATION_INPUT_WIDTH = 104;
const ZABAN_FIELD_CONTENT_WIDTH = 106;

// Shell widths match the visible control so each value sits on the column center.
// The expense-type shell fills its column, which grows on a wide screen.
const AMOUNT_SHELL_WIDTH = AMOUNT_INPUT_WIDTH;
const DATE_SHELL_WIDTH = DATE_INPUT_WIDTH;
const ZABAN_SHELL_WIDTH = ZABAN_FIELD_CONTENT_WIDTH;
const TABLE_SCROLL_MEDIA_QUERY = "@media (max-width: 1280px)";
const EXPENSE_ROW_FLASH_STYLES = buildHaklaiRowFlashStyles();

export const EXPENSES_TABLE_WIDTH = EXPENSE_TYPE_COLUMN_WIDTH
  + HAKLAI_SECTION_LABEL_COLUMN_WIDTH
  + AMOUNT_COLUMN_WIDTH
  + CURRENCY_COLUMN_WIDTH
  + DATE_COLUMN_WIDTH
  + DEPRECIATION_COLUMN_WIDTH
  + DEPRECIATION_RESULT_COLUMN_WIDTH
  + ACTION_COLUMN_WIDTH;
export const EXPENSES_TABLE_WIDTH_WITH_ZABAN = EXPENSES_TABLE_WIDTH + ZABAN_COLUMN_WIDTH;
const EXPENSES_CELL_HORIZONTAL_PADDING = 8;

export const getHaklaiExpensesTableWidth = ({
  showZabanColumn = false,
  showDepreciationColumns = false,
  currencyColumnWidth = CURRENCY_COLUMN_WIDTH
} = {}) => {
  const columnCount = 6
    + (showZabanColumn ? 1 : 0)
    + (showDepreciationColumns ? 2 : 0);
  const contentWidth = (showZabanColumn ? EXPENSES_TABLE_WIDTH_WITH_ZABAN : EXPENSES_TABLE_WIDTH)
    - CURRENCY_COLUMN_WIDTH
    + currencyColumnWidth
    - (showDepreciationColumns ? 0 : DEPRECIATION_COLUMN_WIDTH + DEPRECIATION_RESULT_COLUMN_WIDTH);
  return contentWidth + (columnCount * EXPENSES_CELL_HORIZONTAL_PADDING);
};
export const EXPENSES_SECTION_TRANSITION_MS = 180;

export const useHaklaiExpensesStyles = makeStyles((theme) => ({
  ...buildHaklaiSectionChromeStyles(theme, {
    sectionContent: {
      padding: "16px 12px"
    }
  }),
  ...haklaiLockableSectionStyles,
  tableWrapper: {
    ...haklaiTableWrapperBaseStyles,
    position: "relative",
    zIndex: 0,
    display: "block",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    marginTop: 0,
    border: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    [TABLE_SCROLL_MEDIA_QUERY]: {
      display: "block",
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch"
    },
    [theme.breakpoints.down("xs")]: {
      "--haklai-nav-height": "56px"
    }
  },
  tableWrapperScrollable: {
    display: "block",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch"
  },
  tableFrameAlign: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%"
  },
  stickyHeader: {
    "& .MuiTableCell-head": {
      ...haklaiStickyHeaderStyles,
      backgroundColor: "#d9e7f2",
      zIndex: 2
    },
    [TABLE_SCROLL_MEDIA_QUERY]: {
      "& .MuiTableCell-head": {
        position: "static"
      }
    }
  },
  stickyHeaderDisabled: {
    "& .MuiTableCell-head": {
      position: "static"
    }
  },
  headerLabelText: {
    color: "#1e3d66",
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "inherit",
    lineHeight: 1.3,
    whiteSpace: "normal",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  tableInner: {
    tableLayout: "fixed",
    borderCollapse: "separate",
    borderSpacing: 0,
    backgroundColor: "transparent",
    "& .MuiTableCell-root": {
      textAlign: "center"
    }
  },
  groupTitleCell: {
    backgroundColor: "#fff6e4 !important",
    borderBottom: "0 !important",
    padding: "14px 16px !important",
    textAlign: "right !important"
  },
  groupTitleText: {
    color: "#1e3d66",
    fontSize: "15px",
    fontWeight: 800,
    lineHeight: 1.3,
    textAlign: "right"
  },
  assetHeaderLeadContent: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    gap: "2px 5px",
    maxWidth: "100%",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  assetHeaderLeadText: {
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word"
  },
  assetHeaderCommercialBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1px 6px",
    maxWidth: "100%",
    borderRadius: 999,
    border: "1px solid #1e3d66",
    backgroundColor: "rgba(30, 61, 102, 0.08)",
    color: "#1e3d66",
    fontSize: "0.66rem",
    fontWeight: 800,
    lineHeight: 1.25,
    whiteSpace: "nowrap"
  },
  emptyStateCell: {
    backgroundColor: "transparent",
    padding: "12px 10px",
    textAlign: "center"
  },
  emptyStateText: {
    color: "#8a939d",
    fontSize: "0.84rem",
    fontWeight: 600,
    lineHeight: 1.35
  },
  expenseDataRow: {
    "& > .MuiTableCell-root": {
      paddingTop: 7,
      paddingBottom: 7
    }
  },
  columnFieldWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    maxWidth: "100%",
    margin: "0 auto",
    padding: 0
  },
  expenseTypeFieldWrap: {
    width: "100%",
    maxWidth: "100%"
  },
  expenseTypeTooltipAnchor: {
    display: "block",
    maxWidth: "100%",
    width: "100%"
  },
  amountFieldWrap: {
    width: AMOUNT_SHELL_WIDTH
  },
  currencyFieldWrap: {
    width: "auto"
  },
  dateFieldWrap: {
    width: DATE_SHELL_WIDTH
  },
  depreciationFieldWrap: {
    width: DEPRECIATION_COLUMN_WIDTH - 10
  },
  depreciationResultFieldWrap: {
    width: "max-content",
    maxWidth: "100%"
  },
  zabanFieldWrap: {
    width: ZABAN_SHELL_WIDTH
  },
  // Shell wrappers size to the visible control. The shared status "!" is pulled
  // out of flow below so values stay centered under each header.
  expenseTypeFieldShell: {
    width: "100%",
    maxWidth: "100%",
    "& $fieldShellStatusSlot": {
      display: "none"
    },
    "& $fieldShellInputSlot": {
      width: "100%"
    }
  },
  amountFieldShell: {
    width: AMOUNT_SHELL_WIDTH,
    maxWidth: "100%"
  },
  currencyFieldShell: {
    width: "auto",
    maxWidth: "100%"
  },
  dateFieldShell: {
    width: DATE_SHELL_WIDTH,
    maxWidth: "100%"
  },
  zabanFieldShell: {
    width: ZABAN_SHELL_WIDTH,
    maxWidth: "100%"
  },
  depreciationResultFieldShell: {
    width: "max-content",
    maxWidth: "100%",
    "& $fieldShellInputSlot": {
      flex: "0 0 auto",
      width: "max-content",
      maxWidth: "100%"
    },
    "& $fieldShellStatusSlot": {
      display: "none"
    }
  },
  depreciationResultFieldShellWithError: {
    width: "max-content",
    maxWidth: "100%"
  },
  expenseTypeSelectInput: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: customColors.white,
      borderRadius: 20
    },
    "& .MuiSelect-select": {
      paddingTop: 5,
      paddingBottom: 5,
      paddingLeft: 24,
      paddingRight: 8,
      fontSize: "0.85rem",
      textAlign: "right",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    "& .MuiSelect-icon": {
      left: 4,
      right: "auto"
    }
  },
  expenseTypeValue: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    textAlign: "right"
  },
  amountInput: {
    width: AMOUNT_INPUT_WIDTH,
    "& input": {
      textAlign: "center",
      padding: "6px 8px",
      fontSize: "0.85rem"
    }
  },
  currencySelectInput: {
    width: "auto",
    "& .MuiOutlinedInput-root": {
      background: "linear-gradient(90deg, #173a68 0%, #24558d 100%)",
      borderRadius: 20,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent"
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent"
      }
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent"
    },
    "& .MuiSvgIcon-root": {
      color: "#ffffff"
    },
    "& .MuiSelect-icon": {
      color: "#ffffff",
      left: 2,
      right: "auto"
    },
    "& .MuiSelect-select": {
      padding: "5px 6px 5px 18px",
      fontSize: "0.85rem",
      textAlign: "right",
      color: "#ffffff",
      whiteSpace: "nowrap",
      textOverflow: "clip"
    }
  },
  editableCurrencySelectInput: {},
  disabledInlineField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: customColors.veryLigntGray,
      borderRadius: 20,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(120, 144, 156, 0.28)"
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(120, 144, 156, 0.28)"
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(120, 144, 156, 0.28)"
      },
      "&.Mui-disabled": {
        backgroundColor: customColors.veryLigntGray
      },
      "&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(120, 144, 156, 0.28)"
      }
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: customColors.darkGray,
      WebkitTextFillColor: customColors.darkGray,
      cursor: "default"
    }
  },
  disabledCurrencySelectInput: {
    "& .MuiOutlinedInput-root": {
      background: "#eef3f7"
    },
    "& .MuiSelect-select, & .MuiSelect-select.Mui-disabled": {
      color: customColors.darkGray,
      WebkitTextFillColor: customColors.darkGray,
      padding: "5px 8px !important",
      textAlign: "center"
    },
    "& .MuiSelect-icon, & .MuiSelect-icon.Mui-disabled": {
      display: "none"
    }
  },
  dateInput: {
    width: "100%",
    "& .MuiInputBase-root": {
      paddingLeft: 0
    },
    "& .MuiOutlinedInput-input, & .MuiInputBase-input": {
      fontSize: "0.85rem",
      padding: "6px 8px 6px 4px !important",
      textAlign: "right"
    },
    "& .MuiInputAdornment-positionStart": {
      marginRight: 0,
      marginLeft: 0
    },
    "& .MuiIconButton-root": {
      padding: 2
    }
  },
  readOnlyField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fb",
      borderRadius: 20,
      color: customColors.darkGray,
      "& fieldset": {
        borderColor: "rgba(120, 144, 156, 0.28)",
        borderWidth: 1
      },
      "&:hover fieldset": {
        borderColor: "rgba(120, 144, 156, 0.28)"
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(120, 144, 156, 0.28)",
        borderWidth: 1
      }
    },
    "& .MuiInputBase-input": {
      paddingTop: 5,
      paddingBottom: 5,
      paddingInlineStart: 12,
      paddingInlineEnd: 12,
      fontSize: "0.85rem",
      color: customColors.darkGray,
      cursor: "default",
      textAlign: "center"
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: customColors.darkGray,
      WebkitTextFillColor: customColors.darkGray
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 4,
      marginRight: 2,
      "& p": {
        fontSize: "0.72rem"
      }
    }
  },
  typeReadOnlyField: {
    width: "100%",
    "& .MuiInputBase-input": {
      textAlign: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }
  },
  dateReadOnlyField: {
    width: DATE_INPUT_WIDTH,
    "& .MuiOutlinedInput-root": {
      paddingLeft: 0
    },
    "& .MuiInputBase-input": {
      direction: "ltr",
      padding: "5px 2px 5px 6px !important",
      textAlign: "right"
    },
    "& .MuiInputAdornment-positionStart": {
      marginRight: 0,
      marginLeft: 0
    },
    "& .MuiIconButton-root": {
      padding: 2
    }
  },
  disabledDateCalendarButton: {
    padding: 2,
    color: "#a7afb7",
    opacity: 0.72,
    "&.Mui-disabled": {
      color: "#a7afb7"
    }
  },
  depreciationFieldContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    width: "max-content",
    maxWidth: "100%"
  },
  depreciationValueField: {
    width: DEPRECIATION_INPUT_WIDTH,
    "& .MuiInputBase-input": {
      textAlign: "center"
    }
  },
  depreciationResultDisplay: {
    width: "max-content",
    maxWidth: "100%",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    direction: "ltr",
    padding: "5px 8px",
    boxSizing: "border-box",
    color: "#1e3d66",
    cursor: "default"
  },
  depreciationResultDisplayError: {
    color: customColors.red
  },
  depreciationResultDisplayCurrency: {
    flexShrink: 0,
    fontSize: "0.72rem",
    fontWeight: 400,
    lineHeight: 1,
    color: "#7a8fa3"
  },
  depreciationResultDisplayValue: {
    flexShrink: 0,
    overflow: "visible",
    whiteSpace: "nowrap",
    direction: "ltr",
    textAlign: "center",
    fontSize: "0.82rem",
    lineHeight: 1.2,
    fontWeight: 400,
    color: "inherit",
    fontVariantNumeric: "tabular-nums"
  },
  depreciationEditButton: {
    width: "max-content",
    maxWidth: "100%",
    height: 34,
    minHeight: 34,
    borderRadius: 20,
    border: "1px solid #ff9800",
    background: "linear-gradient(180deg, #ffb109 0%, #ff9800 100%)",
    color: "#ffffff",
    padding: "5px 10px",
    boxSizing: "border-box",
    boxShadow: "0 2px 6px rgba(255, 152, 0, 0.28)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "transform"], {
      duration: theme.transitions.duration.shortest
    }),
    "&:hover": {
      borderColor: "#fb8c00",
      background: "linear-gradient(180deg, #ffc107 0%, #fb8c00 100%)",
      boxShadow: "0 3px 8px rgba(255, 152, 0, 0.34)",
      transform: "translateY(-1px)"
    },
    "&:active": {
      boxShadow: "none",
      transform: "translateY(0)"
    },
    "&.Mui-focusVisible": {
      boxShadow: "0 0 0 3px rgba(255, 152, 0, 0.28)"
    }
  },
  depreciationEditButtonEmpty: {},
  depreciationEditButtonResultSize: {
    width: "max-content",
    maxWidth: DEPRECIATION_RESULT_CONTROL_WIDTH
  },
  depreciationEditButtonWarning: {
    "& $depreciationEditButtonText": {
      color: "#ffffff"
    }
  },
  depreciationEditButtonComplete: {
    "& $depreciationEditButtonText": {
      color: "#ffffff"
    }
  },
  depreciationEditButtonDisabled: {
    color: "#8a939d",
    borderColor: "rgba(120, 144, 156, 0.22)",
    backgroundColor: "#f7f9fb",
    cursor: "default",
    boxShadow: "none",
    "& $depreciationEditButtonText": {
      color: "#8a939d"
    },
    "&:hover": {
      borderColor: "rgba(120, 144, 156, 0.22)",
      backgroundColor: "#f7f9fb",
      boxShadow: "none",
      transform: "none"
    }
  },
  depreciationAmountEditButton: {
    width: "max-content",
    maxWidth: DEPRECIATION_RESULT_CONTROL_WIDTH,
    height: 34,
    minHeight: 34,
    borderRadius: 20,
    border: "1px solid #ff9800",
    background: "linear-gradient(180deg, #ffb109 0%, #ff9800 100%)",
    color: "#ffffff",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    direction: "ltr",
    padding: "5px 10px",
    textAlign: "center",
    boxShadow: "0 2px 6px rgba(255, 152, 0, 0.28)",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow"], {
      duration: theme.transitions.duration.shortest
    }),
    "&:hover": {
      borderColor: "#fb8c00",
      background: "linear-gradient(180deg, #ffc107 0%, #fb8c00 100%)",
      boxShadow: "0 3px 8px rgba(255, 152, 0, 0.34)"
    },
    "&.Mui-focusVisible": {
      borderColor: "#ff9800",
      boxShadow: "0 0 0 3px rgba(255, 152, 0, 0.28)"
    }
  },
  depreciationAmountEditButtonCurrency: {
    flexShrink: 0,
    color: "inherit",
    fontSize: "0.72rem",
    fontWeight: 400,
    lineHeight: 1
  },
  depreciationAmountEditButtonValue: {
    flexShrink: 0,
    overflow: "visible",
    whiteSpace: "nowrap",
    direction: "ltr",
    textAlign: "center",
    fontSize: "0.82rem",
    lineHeight: 1.2,
    fontWeight: 400,
    color: "inherit",
    fontVariantNumeric: "tabular-nums"
  },
  depreciationAmountEditButtonIcon: {
    flexShrink: 0,
    fontSize: "0.96rem",
    color: "inherit",
    opacity: 0.82
  },
  depreciationEditButtonText: {
    width: "max-content",
    height: "100%",
    display: "grid",
    direction: "ltr",
    gridTemplateColumns: "max-content 14px",
    alignItems: "center",
    columnGap: 6,
    color: "#ffffff",
    lineHeight: 1,
    whiteSpace: "nowrap",
    textAlign: "center",
    padding: 0,
    boxSizing: "border-box"
  },
  depreciationButtonLeadingSpacer: {
    width: 14,
    height: 14,
    justifySelf: "center"
  },
  depreciationButtonActionText: {
    fontSize: "0.78rem",
    fontWeight: 800,
    color: "inherit",
    lineHeight: 1,
    whiteSpace: "nowrap",
    justifySelf: "center",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  depreciationButtonCalculatorIcon: {
    fontSize: "0.96rem",
    color: "inherit",
    opacity: 0.82,
    justifySelf: "center"
  },
  depreciationCheckboxContent: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    maxWidth: "100%",
    color: customColors.darkGray,
    cursor: "pointer"
  },
  depreciationCheckboxContentDisabled: {
    color: "#9aa5af",
    opacity: 0.62,
    cursor: "default"
  },
  depreciationCheckboxLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    lineHeight: 1.1,
    whiteSpace: "nowrap"
  },
  depreciationCheckboxLabelDisabled: {
    color: "#9aa5af"
  },
  depreciationCheckbox: {
    padding: 2,
    "&.Mui-disabled": {
      color: "#aeb8c2"
    }
  },
  depreciationCheckboxWarning: {
    color: customColors.orange
  },
  inertDisabledField: {
    "& .MuiInputBase-root.Mui-disabled": {
      pointerEvents: "none",
      cursor: "default"
    }
  },
  optionalTrailingField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fbfcfd"
    },
    "& .MuiOutlinedInput-input": {
      color: "#8a939d"
    }
  },
  zabanCheckboxField: {
    width: "100%",
    minHeight: 30,
    padding: "2px 4px",
    borderRadius: 16,
    border: "1px solid transparent",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 2,
    justifyContent: "space-evenly",
    alignItems: "center",
    boxSizing: "border-box"
  },
  zabanCheckboxFieldError: {
    borderColor: customColors.red
  },
  zabanCheckboxFieldMuted: {
    backgroundColor: "#f7f9fb",
    pointerEvents: "none",
    cursor: "default"
  },
  zabanCheckboxCellContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%"
  },
  zabanCheckboxOption: {
    margin: 0,
    justifyContent: "center",
    flex: "0 0 auto",
    whiteSpace: "nowrap",
    "& .MuiFormControlLabel-label": {
      fontSize: "0.78rem",
      fontWeight: 700,
      lineHeight: 1.1,
      color: "#1e3d66"
    },
    "& .MuiCheckbox-root": {
      padding: 2
    }
  },
  zabanCheckboxMutedText: {
    fontSize: "0.8rem",
    color: "#8a939d",
    textAlign: "center"
  },
  nonRelevantCell: {
    backgroundColor: "#f7f9fb"
  },
  nonRelevantText: {
    fontSize: "0.8rem",
    color: "#8a939d",
    textAlign: "center",
    lineHeight: 1.25
  },
  readOnlyCellText: {
    fontSize: "0.8rem",
    fontWeight: 700,
    color: customColors.darkGray,
    textAlign: "center",
    lineHeight: 1.2,
    whiteSpace: "normal"
  },
  mutedCellText: {
    fontSize: "0.8rem",
    color: "#8a939d",
    textAlign: "center"
  },
  explainedMutedCellText: {
    cursor: "help",
    textDecoration: "underline dotted",
    textUnderlineOffset: 3
  },
  actionCell: {
    "&&": {
      padding: 0,
      width: ACTION_COLUMN_WIDTH,
      minWidth: ACTION_COLUMN_WIDTH,
      textAlign: "center",
      verticalAlign: "middle"
    }
  },
  cellCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%"
  },
  rowActionCellContent: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    flexWrap: "nowrap"
  },
  rowAddCell: {
    width: ADD_ROW_COLUMN_WIDTH,
    minWidth: ADD_ROW_COLUMN_WIDTH,
    maxWidth: ADD_ROW_COLUMN_WIDTH,
    padding: "2px !important"
  },
  rowAddCellContent: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  // Field-shell primitives and row flash styles live in
  // `shared/table/haklaiTableShared.js` so both haklai tables share one visual
  // language. Spreading the factories here is what lets `FieldShell` and
  // row flashing work against the expenses classes object without any
  // per-table duplication.
  ...buildHaklaiFieldShellStyles(),
  // Keep the "!" indicator available without shifting the input off the
  // column midpoint under each header.
  fieldShell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    gap: 0,
    maxWidth: "100%",
    margin: "0 auto"
  },
  fieldShellStatusSlot: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    insetInlineStart: -HAKLAI_FIELD_ERROR_SLOT_WIDTH - 2,
    width: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    minWidth: HAKLAI_FIELD_ERROR_SLOT_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 1
  },
  ...EXPENSE_ROW_FLASH_STYLES,
  rowFlash: {
    ...EXPENSE_ROW_FLASH_STYLES.rowFlash,
    "& > .MuiTableCell-root:not([rowspan]) $editableCurrencySelectInput .MuiInputBase-root": {
      animation: "none",
      background: "linear-gradient(90deg, #173a68 0%, #24558d 100%)",
      boxShadow: "none"
    }
  },
  ...haklaiAddPillStyles
}));
